import { Location } from 'history';
import { MapGeoJSONFeature } from 'shared/map-exports';
import { ReportableGeoEventType } from 'features/geoEvents/geoEvent.types';
import {
  DrawerEntityPoiType,
  FeatureLayerGeomType,
  MapboxFeature,
  MapClickGetDrawerEntityForPoi,
  MapClickGetPois,
  MapClickHandler,
  NavigationalMapClickType,
  StandardPoiType,
} from 'features/mapping/mapping.types';
import {
  HAZARD_ICONS_LAYER_ID,
  LOCATION_ICONS_LAYER_ID,
  LOCATION_LABELS_LAYER_ID,
} from 'features/mapping/styles';
import {
  WATER_LEVEL_LOW_ZOOM_LAYER_ID,
  WATER_LEVEL_HIGH_ZOOM_LAYER_ID,
  WATER_LEVEL_NONE_OFFLINE_ICONS_LAYER_ID,
  WATER_LEVEL_LABELS_LAYER_ID,
  waterLevelClickHandler,
  resolveWaterLevelPath,
} from 'features/waterLevel/components/WaterLevelLayer';
import {
  WEATHER_STATIONS_ARROW_ICONS_LAYER_ID,
  WEATHER_STATIONS_POINT_ICONS_LAYER_ID,
} from 'features/weatherStations/components/WeatherStationsLayer/WeatherStationsLayer.constants';
import {
  weatherStationsClickHandler,
  resolveWeatherStationPath,
} from 'features/weatherStations/components/WeatherStationsLayer';
import { RouterEntityDrawer } from '../../../constants';
import {
  COMPLEX_WILDFIRE_ICONS_LAYER_ID,
  COMPLEX_WILDFIRE_LABELS_LAYER_ID,
  COMPLEX_WILDFIRE_PARENT_CHIP_LAYER_ID,
  WILDFIRE_ICONS_LAYER_ID,
  WILDFIRE_LABELS_LAYER_ID,
  wildfiresClickHandler,
  resolveWildfirePath,
} from '../components/layers/WildfiresLayer';
import {
  alertCamerasClickHandler,
  resolveCameraPath,
} from '../components/layers/AlertCamerasLayer';
import {
  PTZ_ARROW_ICONS_LAYER_ID,
  PTZ_ICONS_LAYER_ID,
  RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID,
  RECENTLY_MOVED_PTZ_ICONS_LAYER_ID,
  STATIONARY_ICONS_LAYER_ID,
} from '../components/layers/AlertCamerasLayer/AlertCamerasLayer.constants';
import { EVAC_ZONES_VIEW_FILL_LAYER_ID } from '../components/layers/StructuredEvacuationsLayer/constants';
import { ELECTRIC_RETAIL_LAYER_ID } from '../components/layers/ElectricRetailServiceLayer/constants';
import { RESPONSIBILITY_AREAS_LAYER_ID } from '../components/layers/ResponsibilityAreasLayer/constants';
import { activeEvacZoneClickHandler } from '../components/layers/StructuredEvacuationsLayer/StructuredEvacuationsViewLayer';
import { evacZoneFeatureComparator } from '../components/layers/StructuredEvacuationsLayer/utils';
import { responsibilityAreaClickHandler } from '../components/layers/ResponsibilityAreasLayer';
import { electricRetailServiceClickHandler } from '../components/layers/ElectricRetailServiceLayer';
import { PRIVATE_LAND_OWNERSHIP_FILL_LAYER_ID } from '../components/layers/PrivateLandOwnershipLayer/constants';
import { privateLandOwnershipClickHandler } from '../components/layers/PrivateLandOwnershipLayer';
import { externalFeatureLayerClickHandler } from '../components/layers/ExternalFeatureLayer';
import {
  ACTIVE_FLOODING_ICONS_LAYER_ID,
  ACTIVE_FLOODING_LABELS_LAYER_ID,
  INACTIVE_FLOODING_ICONS_LAYER_ID,
  INACTIVE_FLOODING_LABELS_LAYER_ID,
  floodingsClickHandler,
  resolveFloodingPath,
} from '../components/layers/FloodingsLayer';
import {
  locationClickHandler,
  resolveLocationPath,
} from '../components/layers/LocationsLayer';
import {
  hazardClickHandler,
  resolveHazardPath,
} from '../components/layers/HazardsLayer';
import {
  AQI_POINT_BLURS_LAYER_ID,
  AQI_POINT_ICONS_LAYER_ID,
} from '../components/layers/PurpleAirLayer/AQILayer.constants';
import {
  aqiClickHandler,
  resolveAqiPath,
} from '../components/layers/PurpleAirLayer/AQILayer';
import {
  EXTERNAL_GEO_EVENT_ICONS_LAYER_ID,
  externalGeoEventsClickHandler,
} from '../components/layers/ExternalGeoEventsLayer';
import {
  PLACE_PIN_ICONS_LAYER_ID,
  placeClickHandler,
  placePoiClickHandler,
  resolvePlacePath,
} from '../components/layers/PlacesLayer';
import {
  FIRE_PERIMETERS_FILL_LAYER_ID,
  firePerimeterClickHandler,
} from '../components/layers/FirePerimetersLayer';
import {
  EVAC_ZONE_FILL_LAYER_ID,
  evacZoneClickHandler,
} from '../components/layers/StructuredEvacuationsLayer/StructuredEvacuationsAllLayer';
import {
  ELECTRICAL_MAP_INTERACTIVE_LAYER_ID,
  GAS_MAP_INTERACTIVE_LAYER_ID,
} from '../components/layers/TransmissionLinesLayer/constants';
import {
  electricLineClickHandler,
  gasPipelineClickHandler,
} from '../components/layers/TransmissionLinesLayer';
import {
  RADIO_TOWER_ICON_LAYER_ID,
  radioTowerClickHandler,
} from '../components/layers/RadioTowersLayer';
import {
  ALERT_CLICKABLE_LAYER_ID,
  nwsAlertClickHandler,
  nwsAlertFeatureComparator,
  resolveNwsAlertPath,
} from '../components/layers/NWSWatchesAndWarningsLayer';
import {
  AREA_FILL_LAYER_ID as POWER_OUTAGE_AREA_FILL_LAYER_ID,
  AREA_STROKE_LAYER_ID as POWER_OUTAGE_AREA_STROKE_LAYER_ID,
  ITEM_BACKGROUND_LAYER_ID as POWER_OUTAGE_ITEM_BACKGROUND_LAYER_ID,
  ITEM_ICON_LAYER_ID as POWER_OUTAGE_ITEM_ICON_LAYER_ID,
  powerOutageClickHandler,
} from '../components/layers/PowerOutageLayer';
import {
  ITEM_ICON_LAYER_ID as LIGHTNING_ITEM_ICON_LAYER_ID,
  lightningClickHandler,
} from '../components/layers/LightningLayer';
import {
  PHOTO_MARKERS_LAYER_ID,
  PhotoMarkerFeatureProperties,
  photoMarkerClickHandler,
} from '../components/layers/PhotoMarkersLayer';
import {
  REPORTS_ICONS_FULL_LAYER_ID,
  REPORTS_ICONS_LAYER_ID,
  ReportFeatureProperties,
  reportClickHandler,
} from '../components/layers/ReportsLayer';
import {
  FEMA_FLOOD_HAZARD_FILL_LAYER_ID,
  femaFloodHazardClickHandler,
} from '../components/layers/FemaFloodHazardLayer';
import {
  FILL_LAYER_ID as NGFS_DETECTIONS_FILL_LAYER_ID,
  ngfsDetectionClickHandler,
} from '../components/layers/NgfsDetectionsLayer';

/** Layer ids grouped by the geometry they contribute, for a type split across layers. */
export type ClickGeometryLayerIds = Partial<
  Record<FeatureLayerGeomType, readonly string[]>
>;

/**
 * How a type's click geometry is determined: a fixed `FeatureLayerGeomType`, its layer ids grouped
 * by geometry when one type spans layers of different geometry, or `fromLayerMetadata` for
 * external layers that carry their geometry with them.
 */
export type DeclaredClickGeometry =
  | FeatureLayerGeomType
  | 'fromLayerMetadata'
  | { layerIds: ClickGeometryLayerIds };

type RouterClickGeometry = Extract<FeatureLayerGeomType, 'point' | 'polygon'>;

const POWER_OUTAGE_CLICK_GEOMETRY = {
  layerIds: {
    polygon: [
      POWER_OUTAGE_AREA_FILL_LAYER_ID,
      POWER_OUTAGE_AREA_STROKE_LAYER_ID,
    ],
    point: [
      POWER_OUTAGE_ITEM_BACKGROUND_LAYER_ID,
      POWER_OUTAGE_ITEM_ICON_LAYER_ID,
    ],
  },
} as const satisfies DeclaredClickGeometry;

export const INTERACTIVE_LAYER_IDS_BY_DRAWER_OR_POI: Record<
  | RouterEntityDrawer
  | StandardPoiType
  | DrawerEntityPoiType
  | 'report'
  | 'photoMarker',
  string[]
> = {
  // Router Entity Drawers
  wildfire: [
    WILDFIRE_ICONS_LAYER_ID,
    COMPLEX_WILDFIRE_ICONS_LAYER_ID,
    WILDFIRE_LABELS_LAYER_ID,
    COMPLEX_WILDFIRE_LABELS_LAYER_ID,
    COMPLEX_WILDFIRE_PARENT_CHIP_LAYER_ID,
  ],
  flooding: [
    ACTIVE_FLOODING_ICONS_LAYER_ID,
    ACTIVE_FLOODING_LABELS_LAYER_ID,
    INACTIVE_FLOODING_ICONS_LAYER_ID,
    INACTIVE_FLOODING_LABELS_LAYER_ID,
  ],
  location: [LOCATION_ICONS_LAYER_ID, LOCATION_LABELS_LAYER_ID],
  hazard: [HAZARD_ICONS_LAYER_ID],
  camera: [
    PTZ_ICONS_LAYER_ID,
    PTZ_ARROW_ICONS_LAYER_ID,
    RECENTLY_MOVED_PTZ_ICONS_LAYER_ID,
    RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID,
    STATIONARY_ICONS_LAYER_ID,
  ],
  gauge: [
    WATER_LEVEL_LOW_ZOOM_LAYER_ID,
    WATER_LEVEL_HIGH_ZOOM_LAYER_ID,
    WATER_LEVEL_NONE_OFFLINE_ICONS_LAYER_ID,
    WATER_LEVEL_LABELS_LAYER_ID,
  ],
  weatherStation: [
    WEATHER_STATIONS_POINT_ICONS_LAYER_ID,
    WEATHER_STATIONS_ARROW_ICONS_LAYER_ID,
  ],
  aqi: [AQI_POINT_BLURS_LAYER_ID, AQI_POINT_ICONS_LAYER_ID],
  alert: [ALERT_CLICKABLE_LAYER_ID],
  // Standard POI Types
  externalGeoEvent: [EXTERNAL_GEO_EVENT_ICONS_LAYER_ID],
  activeEvacZone: [EVAC_ZONES_VIEW_FILL_LAYER_ID],
  evacZone: [EVAC_ZONE_FILL_LAYER_ID],
  firePerimeter: [FIRE_PERIMETERS_FILL_LAYER_ID],
  place: [PLACE_PIN_ICONS_LAYER_ID],
  // No layer of its own: `gatherClickFeatures` remaps `place` to `legacyPlace` when
  // `pin_drop_improvements` is off.
  legacyPlace: [],
  powerOutage: Object.values(POWER_OUTAGE_CLICK_GEOMETRY.layerIds).flat(),
  lightning: [LIGHTNING_ITEM_ICON_LAYER_ID],
  electricalLine: [ELECTRICAL_MAP_INTERACTIVE_LAYER_ID],
  gasPipeline: [GAS_MAP_INTERACTIVE_LAYER_ID],
  radioTower: [RADIO_TOWER_ICON_LAYER_ID],
  electricRetail: [ELECTRIC_RETAIL_LAYER_ID],
  responsibilityArea: [RESPONSIBILITY_AREAS_LAYER_ID],
  femaFloodHazard: [FEMA_FLOOD_HAZARD_FILL_LAYER_ID],
  ngfsDetection: [NGFS_DETECTIONS_FILL_LAYER_ID],
  // Drawer Entity POIs
  privateLandOwnership: [PRIVATE_LAND_OWNERSHIP_FILL_LAYER_ID],
  // Dynamic layer IDs for external feature layers are handled in the hook directly
  basic: [],
  overstory: [],
  // Report layer
  report: [REPORTS_ICONS_LAYER_ID, REPORTS_ICONS_FULL_LAYER_ID],
  // Photo marker layer
  photoMarker: [PHOTO_MARKERS_LAYER_ID],
};

export const INTERACTIVE_LAYER_IDS = Object.values(
  INTERACTIVE_LAYER_IDS_BY_DRAWER_OR_POI,
).flat();

export const ROUTER_ENTITY_DRAWER_HANDLERS: Record<
  RouterEntityDrawer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MapClickHandler<any>
> = {
  wildfire: wildfiresClickHandler,
  flooding: floodingsClickHandler,
  location: locationClickHandler,
  hazard: hazardClickHandler,
  camera: alertCamerasClickHandler,
  gauge: waterLevelClickHandler,
  weatherStation: weatherStationsClickHandler,
  aqi: aqiClickHandler,
  alert: nwsAlertClickHandler,
  place: placeClickHandler,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const POI_HANDLERS: Record<StandardPoiType, MapClickGetPois<any>> = {
  externalGeoEvent: externalGeoEventsClickHandler,
  activeEvacZone: activeEvacZoneClickHandler,
  evacZone: evacZoneClickHandler,
  firePerimeter: firePerimeterClickHandler,
  legacyPlace: placePoiClickHandler,
  powerOutage: powerOutageClickHandler,
  lightning: lightningClickHandler,
  electricalLine: electricLineClickHandler,
  gasPipeline: gasPipelineClickHandler,
  radioTower: radioTowerClickHandler,
  electricRetail: electricRetailServiceClickHandler,
  responsibilityArea: responsibilityAreaClickHandler,
  femaFloodHazard: femaFloodHazardClickHandler,
  ngfsDetection: ngfsDetectionClickHandler,
};

export const ENTITY_DRAWER_POI_HANDLERS: Record<
  DrawerEntityPoiType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MapClickGetDrawerEntityForPoi<any>
> = {
  privateLandOwnership: privateLandOwnershipClickHandler,
  basic: externalFeatureLayerClickHandler,
  overstory: externalFeatureLayerClickHandler,
};

/** Runtime context the navigational handlers need but cannot derive themselves. */
export type NavigationalClickContext = {
  /** Current router location; used to build the `fromRoute` back-navigation state. */
  route: Location;
  /** Report incident type; falls back to `'wildfire'` when no incident is selected. */
  reportGeoEventType: ReportableGeoEventType;
};

/**
 * Navigational click handlers. Unlike the POI / router maps these are not run to produce data:
 * firing one navigates away, so `buildClickCandidates` only wraps them in a target's `run()`.
 */
export const NAVIGATIONAL_HANDLERS: Record<
  NavigationalMapClickType,
  (features: MapGeoJSONFeature[], ctx: NavigationalClickContext) => void
> = {
  photoMarker: (features) =>
    photoMarkerClickHandler(
      features as MapboxFeature<PhotoMarkerFeatureProperties>[],
    ),
  report: (features, { route, reportGeoEventType }) =>
    reportClickHandler(
      features as MapboxFeature<ReportFeatureProperties>[],
      route,
      reportGeoEventType,
    ),
};

export const CLICK_GEOMETRY_BY_ROUTER_ENTITY: Record<
  RouterEntityDrawer,
  RouterClickGeometry
> = {
  wildfire: 'point',
  flooding: 'point',
  location: 'point',
  hazard: 'point',
  place: 'point',
  camera: 'point',
  gauge: 'point',
  weatherStation: 'point',
  aqi: 'point',
  alert: 'polygon',
};

export const CLICK_GEOMETRY_BY_POI: Record<
  StandardPoiType,
  DeclaredClickGeometry
> = {
  externalGeoEvent: 'point',
  activeEvacZone: 'polygon',
  evacZone: 'polygon',
  firePerimeter: 'polygon',
  legacyPlace: 'point',
  powerOutage: POWER_OUTAGE_CLICK_GEOMETRY,
  lightning: 'point',
  electricalLine: 'line',
  gasPipeline: 'line',
  radioTower: 'point',
  electricRetail: 'polygon',
  responsibilityArea: 'polygon',
  femaFloodHazard: 'polygon',
  ngfsDetection: 'polygon',
};

export const CLICK_GEOMETRY_BY_DRAWER_ENTITY_POI: Record<
  DrawerEntityPoiType,
  DeclaredClickGeometry
> = {
  privateLandOwnership: 'polygon',
  basic: 'fromLayerMetadata',
  overstory: 'fromLayerMetadata',
};

export const ROUTER_ENTITY_PATH_RESOLVERS: Record<
  RouterEntityDrawer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (features: any[]) => string | null
> = {
  wildfire: resolveWildfirePath,
  flooding: resolveFloodingPath,
  location: resolveLocationPath,
  hazard: resolveHazardPath,
  camera: resolveCameraPath,
  gauge: resolveWaterLevelPath,
  weatherStation: resolveWeatherStationPath,
  aqi: resolveAqiPath,
  alert: resolveNwsAlertPath,
  place: resolvePlacePath,
};

/**
 * Optional per-entity-type comparators for ordering features of the same type
 * within stacked navigation.
 */
export const ROUTER_ENTITY_FEATURE_COMPARATORS: Partial<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<RouterEntityDrawer, (a: any, b: any) => number>
> = {
  alert: nwsAlertFeatureComparator,
};

/**
 * Optional per-POI-type comparators for ordering features of the same type
 * within the POI dialog / stacked navigation.
 */
export const POI_FEATURE_COMPARATORS: Partial<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<StandardPoiType, (a: any, b: any) => number>
> = {
  activeEvacZone: evacZoneFeatureComparator,
  evacZone: evacZoneFeatureComparator,
};
